/**
 * The rules of the one notice system (Robert approved the design on
 * 2026-11-02: ~/dev/scratch/mockups/notifications/notifications-notes.md).
 *
 * Pure functions only: no DOM, no timers, no clock. The vanilla pages use
 * them through shared/notice-host.js (window.MalkaNoticeRules); the React web
 * parts (Relay web, the call room's notifications) import this file.
 *
 * Variants:
 *   info     A fact the screen does not show. 5 s, 8 s for more than 8 words.
 *   success  Only when the screen does not show the result, or with Undo.
 *            5 s, 8 s with an action.
 *   error    Something failed. One action or a close button. No timeout.
 *            role=alert, aria-live assertive.
 *   banner   A state that is true now ("No connection"). In flow under the
 *            header. It shows while the state is true. No close button.
 *
 * Stacking: one transient notice and one banner at a time. A new notice of
 * the same or a higher priority replaces the current one (error > banner >
 * info and success); the replaced one waits only when it has an action. A
 * lower one waits in a queue of at most 3; a duplicate
 * id replaces its queued copy; a queued info notice older than 10 s is
 * dropped.
 */
(function(root, factory) {
    const rules = factory();
    if (typeof module === 'object' && module.exports) module.exports = rules;
    else root.MalkaNoticeRules = rules;
})(typeof globalThis !== 'undefined' ? globalThis : this, function() {
    'use strict';

    const VARIANTS = [ 'info', 'success', 'error', 'banner' ];
    const SHORT_MS = 5000;
    const LONG_MS = 8000;
    const LONG_TEXT_WORDS = 8;
    const QUEUE_LIMIT = 3;
    const QUEUED_INFO_MAX_AGE_MS = 10000;
    const PRIORITY = { error: 3, banner: 2, info: 1, success: 1 };
    const MOTION = { enterMs: 200, exitMs: 150, slidePx: 12 };
    const REDUCED_MOTION = { enterMs: 150, exitMs: 150, slidePx: 0 };

    function variantOf(notice) {
        const variant = notice && notice.variant;

        return VARIANTS.includes(variant) ? variant : 'info';
    }

    function wordCount(text) {
        return String(text || '').trim().split(/\s+/).filter(Boolean).length;
    }

    function hasAction(notice) {
        return Boolean(notice && notice.actionLabel);
    }

    /**
     * How long a notice shows, in ms. 0 = it stays until the person acts or
     * the state ends. With a screen reader on, a notice with an action does
     * not time out.
     */
    function noticeDuration(notice, options) {
        const variant = variantOf(notice);
        if (variant === 'error' || variant === 'banner') return 0;
        if (hasAction(notice) && options && options.screenReader) return 0;
        if (variant === 'success' && hasAction(notice)) return LONG_MS;

        return wordCount(notice && notice.text) > LONG_TEXT_WORDS ? LONG_MS : SHORT_MS;
    }

    function noticePriority(notice) {
        return PRIORITY[variantOf(notice)];
    }

    /** The role and live level a notice gets. Errors interrupt; the rest wait. */
    function noticeAria(notice) {
        return variantOf(notice) === 'error'
            ? { role: 'alert', live: 'assertive' }
            : { role: 'status', live: 'polite' };
    }

    /** In 200 ms with a 12 px slide, out in 150 ms; Reduce Motion: fade only. */
    function noticeMotion(reducedMotion) {
        return { ...(reducedMotion ? REDUCED_MOTION : MOTION) };
    }

    /**
     * A notice as the queue keeps it. `id` names the job ("request-outcome");
     * without one the variant and the text do.
     */
    function normalizeNotice(input, now) {
        const notice = input || {};
        const variant = variantOf(notice);
        const text = String(notice.text || '').trim();

        return {
            ...notice,
            variant,
            text,
            id: notice.id ? String(notice.id) : `${variant}:${text}`,
            at: Number.isFinite(notice.at) ? notice.at : now
        };
    }

    function emptyNoticeState() {
        return { current: null, banner: null, queue: [] };
    }

    function stillFresh(notice, now) {
        return variantOf(notice) !== 'info' || !Number.isFinite(now) || now - notice.at <= QUEUED_INFO_MAX_AGE_MS;
    }

    /** The next queued notice: highest priority first, then oldest first. */
    function promoteNext(state, now) {
        const queue = state.queue.filter(notice => stillFresh(notice, now));
        if (!queue.length) return { ...state, current: null, queue };
        let best = 0;
        queue.forEach((notice, index) => {
            if (noticePriority(notice) > noticePriority(queue[best])) best = index;
        });

        return { ...state, current: queue[best], queue: queue.filter((_notice, index) => index !== best) };
    }

    function enqueue(queue, notice) {
        const next = queue.filter(queued => queued.id !== notice.id);
        next.push(notice);
        // Full: the oldest of the lowest priority makes room.
        while (next.length > QUEUE_LIMIT) {
            let drop = 0;
            next.forEach((queued, index) => {
                if (noticePriority(queued) < noticePriority(next[drop])) drop = index;
            });
            next.splice(drop, 1);
        }

        return next;
    }

    /** Adds a notice. Returns the next state; the input is not changed. */
    function pushNotice(state, input, now) {
        const base = state || emptyNoticeState();
        const notice = normalizeNotice(input, now);
        if (!notice.text) return base;
        if (notice.variant === 'banner') return { ...base, banner: notice };
        const current = base.current;
        const queue = base.queue.filter(queued => queued.id !== notice.id);
        if (!current || current.id === notice.id) return { ...base, current: notice, queue };
        if (noticePriority(notice) >= noticePriority(current)) {
            // A replaced notice with an action (Undo, Admit, Try again) waits
            // in the queue: the person may still need it. Others go.
            return { ...base, current: notice, queue: hasAction(current) ? enqueue(queue, current) : queue };
        }

        return { ...base, queue: enqueue(queue, notice) };
    }

    /** Takes a notice away (acted on, closed, timed out, state over). */
    function dismissNotice(state, id, now) {
        const base = state || emptyNoticeState();
        const key = String(id);
        const banner = base.banner && base.banner.id === key ? null : base.banner;
        const queue = base.queue.filter(queued => queued.id !== key);
        if (base.current && base.current.id === key) return promoteNext({ ...base, banner, queue }, now);

        return { ...base, banner, queue };
    }

    /** Takes away every notice the test matches (for example on leaving the screen). */
    function clearNotices(state, test, now) {
        const base = state || emptyNoticeState();
        const match = typeof test === 'function' ? test : () => true;
        const banner = base.banner && match(base.banner) ? null : base.banner;
        const queue = base.queue.filter(queued => !match(queued));
        if (base.current && match(base.current)) return promoteNext({ ...base, banner, queue }, now);

        return { ...base, banner, queue };
    }

    // ---- The timer: it stops while a pointer or the keyboard focus is on it.
    function startTimer(duration, now) {
        return { duration, remaining: duration, startedAt: now, paused: false };
    }

    function timerRemaining(timer, now) {
        if (!timer) return 0;

        return timer.paused ? timer.remaining : Math.max(0, timer.remaining - (now - timer.startedAt));
    }

    function pauseTimer(timer, now) {
        if (!timer || timer.paused) return timer;

        return { ...timer, remaining: timerRemaining(timer, now), paused: true };
    }

    function resumeTimer(timer, now) {
        if (!timer || !timer.paused) return timer;

        return { ...timer, startedAt: now, paused: false };
    }

    return {
        LONG_MS,
        QUEUE_LIMIT,
        QUEUED_INFO_MAX_AGE_MS,
        SHORT_MS,
        VARIANTS,
        clearNotices,
        dismissNotice,
        emptyNoticeState,
        noticeAria,
        noticeDuration,
        noticeMotion,
        noticePriority,
        normalizeNotice,
        pauseTimer,
        pushNotice,
        resumeTimer,
        startTimer,
        timerRemaining,
        wordCount
    };
});
